import {
  ACCOUNT_FEATURE,
  debounce,
  isNumber,
  noop,
  selectUserId,
  selectUserRolePermissions,
  tryParseJson,
} from '@socialive/common';
import { useFeature } from '@socialive/features';
import PropTypes from 'prop-types';
import PullToRefresh from 'pulltorefreshjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { DEFAULT_FILTER_OPTION, FILTER_STORAGE_KEY } from '~/helpers/constants';
import filterStringBuilder from '~/helpers/filterStringBuilder';

import {
  fetchStreams,
  resetStreamsData,
  selectAccountId,
  selectDescendingSortedStreamsList,
  selectStreamsIsLastPage,
  selectStreamsIsLoading,
} from '~/store';
import '~/style/broadcasts.css';
import BottomScrollListener from '../BottomScrollListener';
import EmptyResult from '../EmptyResult';
import Loader from '../Loader';
import RecordingClickToPlay from '../RecordingClickToPlay';
import RecordingLink from '../RecordingLink';
import Search from '../Search';
import { ChannelSelectWithFilter, UserSelectWithFilter } from '../SelectWithFilter';
import NoPastBroadcastsFound from './NoPastBroadcastsFound';

const SEARCH_MIN_LENGTH = 2;

const DEFAULT_PAGINATION_FILTER_STATE = {
  page: 0,
  search: '',
  filterByHost: DEFAULT_FILTER_OPTION.HOST,
  filterByChannel: DEFAULT_FILTER_OPTION.CHANNEL,
};

const PastBroadcasts = ({
  appLogin,
  getScreenOrientation,
  isWebview,
  loadBroadcasts,
  reAuth,
  showHeader,
  trackEvent,
}) => {
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);
  const userId = useSelector(selectUserId);
  const streams = useSelector(selectDescendingSortedStreamsList);
  const isStreamsLoading = useSelector(selectStreamsIsLoading);
  const isLastPage = useSelector(selectStreamsIsLastPage);
  const { canViewOwnRecordings, canViewAllRecordings } = useSelector(selectUserRolePermissions);

  const [searchInput, setSearchInput] = useState('');

  const [channelFilter, setChannelFilter] = useState(() => {
    const savedItem =
      sessionStorage.getItem(FILTER_STORAGE_KEY.CHANNEL) ?? localStorage.getItem(FILTER_STORAGE_KEY.CHANNEL);
    if (savedItem && isNumber(+savedItem)) return +savedItem;
    return DEFAULT_FILTER_OPTION.CHANNEL;
  });

  const [hostFilter, setHostFilter] = useState(() => {
    const savedItem = tryParseJson(sessionStorage.getItem(FILTER_STORAGE_KEY.HOST), DEFAULT_FILTER_OPTION.HOST);
    if (savedItem && isNumber(+savedItem?.value)) return savedItem;
    return DEFAULT_FILTER_OPTION.HOST;
  });

  const [mobileLoginDate, setMobileLoginDate] = useState(0);
  const [, { enabled: mediaUploadEnabled }] = useFeature(ACCOUNT_FEATURE.MEDIA_UPLOAD);
  const streamParamsRef = useRef(DEFAULT_PAGINATION_FILTER_STATE);

  const updateStreamParams = useCallback((params = {}) => {
    streamParamsRef.current = {
      ...streamParamsRef.current,
      ...params,
    };
  }, []);

  const pageSize = isWebview ? 10 : 30;
  const isMobileFirstLoad = isWebview && !mobileLoginDate;

  const resetPlayerSource = useCallback((el) => {
    const { src } = el;
    const parentDiv = el.closest('.stream-player-container');
    const targetClass = parentDiv.className;

    el.classList.remove('loaded');
    el.removeAttribute('src');
    el.setAttribute('src', src);
    el.removeAttribute('controls');

    parentDiv.className = targetClass.replace(' hide-duration', '');
  }, []);

  const appUserLeft = useCallback(() => {
    const videoElements = document.querySelectorAll('.video-js.loaded');

    if (videoElements.length > 0) {
      videoElements.forEach((el) => {
        resetPlayerSource(el);
      });
    }
  }, [resetPlayerSource]);

  const handleOrientation = useCallback(() => {
    const documentBody = document.querySelector('body');
    const screenOrientation = getScreenOrientation();

    if (documentBody) {
      documentBody.classList.remove('portrait', 'landscape');
      if (screenOrientation === 0) {
        documentBody.classList.add('portrait');
      } else if (screenOrientation === 90 || screenOrientation === -90) {
        documentBody.classList.add('landscape');
      }
    }
  }, [getScreenOrientation]);

  const loadStreams = useCallback(async () => {
    const params = streamParamsRef.current;
    const searchText = params?.search ?? '';
    const page = params?.page ?? 0;
    const filterByHost = params?.filterByHost?.value ?? '';
    const filterByChannel = params?.filterByChannel ?? '';

    const isSearching = searchText.length >= SEARCH_MIN_LENGTH;

    const filterObject = {
      'remoteStream.id': filterByChannel !== DEFAULT_FILTER_OPTION.CHANNEL ? filterByChannel : '',
      'user.id': filterByHost !== DEFAULT_FILTER_OPTION.HOST.value ? filterByHost : '',
      title: isSearching ? `"*${encodeURIComponent(searchText)}*"` : '',
    };

    if (userId && canViewOwnRecordings && !canViewAllRecordings) {
      filterObject['user.id'] = userId;
    }

    const query = filterStringBuilder(filterObject, false);

    const options = {
      sort: 'startedAt,desc',
      size: pageSize,
      page,
    };

    if (!isWebview && !isSearching) {
      options.group = 'broadcast';
    }

    const { error } = await dispatch(fetchStreams({ query, options, trigger: 'PastBroadcasts@loadStreams' }));

    if (error?.status === 401) {
      trackEvent('401', {
        source: 'fetchViewData (PastBroadcasts)',
      });
      reAuth(isWebview ? '/webview/recordings' : '/recordings');
      return;
    }

    if (!error) {
      // set pagination for next time this is callback is called.
      updateStreamParams({ page: page + 1 });
    }
  }, [
    dispatch,
    userId,
    isWebview,
    pageSize,
    updateStreamParams,
    reAuth,
    trackEvent,
    canViewOwnRecordings,
    canViewAllRecordings,
  ]);

  const debouncedLoadStreams = useMemo(() => debounce((params) => loadStreams(params), 1000), [loadStreams]);

  const handleSearchChange = useCallback(
    (e) => {
      const text = e.target.value.replace(/"/g, '');
      setSearchInput(text);

      if (text.length === 0 || text.length >= SEARCH_MIN_LENGTH) {
        updateStreamParams({ search: text, page: 0 });
        debouncedLoadStreams();
      }
    },
    [debouncedLoadStreams, updateStreamParams]
  );

  useEffect(() => {
    updateStreamParams({ filterByHost: hostFilter, filterByChannel: channelFilter, page: 0 });
    debouncedLoadStreams();
  }, [channelFilter, hostFilter, debouncedLoadStreams, updateStreamParams]);

  const resetComponent = useCallback(() => {
    setSearchInput('');
    setChannelFilter(DEFAULT_FILTER_OPTION.CHANNEL);
    setHostFilter(DEFAULT_FILTER_OPTION.HOST);

    streamParamsRef.current = DEFAULT_PAGINATION_FILTER_STATE;

    debouncedLoadStreams();
  }, [debouncedLoadStreams]);

  const mobileAuthenticate = useCallback(async () => {
    if (window.SLExternalProperties && window.SLExternalProperties !== 'Not found') {
      const data = JSON.parse(window.SLExternalProperties);

      await appLogin(data, true, '/webview/recordings', data.accountId);
      setMobileLoginDate(Date.now());
    } else {
      /* This alert is used in both mobile apps.
         If the user logs out on portal, their auth is nuked.
         This alert covers that flow so there is a graceful fail.
      */
      // eslint-disable-next-line no-alert
      alert('Authorization failed.');
    }
  }, [appLogin]);

  useEffect(() => {
    if (!isWebview) return noop;
    // Mobile Only effect

    handleOrientation();
    showHeader(false);

    const documentBody = document.querySelector('body');
    if (documentBody) {
      documentBody.classList.add('webview');
    }

    window.Intercom('update', { hide_default_launcher: true });

    const gestureStart = () => {
      const metafields = document.getElementsByTagName('meta');
      const viewportMeta = [...metafields].find(({ name }) => name === 'viewport');
      if (viewportMeta) {
        viewportMeta.content = 'width=device-width, minimum-scale=0.25, maximum-scale=1.6';
      }
    };

    const newPlayerPlayingEvent = (event) => {
      document.querySelectorAll('.video-js.loaded').forEach((el) => {
        if (el.id !== event.detail) {
          resetPlayerSource(el);
        }
      });
    };

    const viewportMeta = document.querySelector('meta[name=viewport]');
    if (viewportMeta) {
      viewportMeta.content = 'width=device-width, minimum-scale=1.0, maximum-scale=1.0';
    }

    if (documentBody) {
      documentBody.addEventListener('gesturestart', gestureStart, false);
    }

    window.addEventListener('mobileAuth', mobileAuthenticate, false);
    window.addEventListener('appUserLeft', appUserLeft, false);
    window.addEventListener('orientationchange', handleOrientation);
    window.addEventListener('newPlayerPlaying', newPlayerPlayingEvent);

    const debouncedReset = debounce(() => {
      resetComponent();
    }, 1000);

    PullToRefresh.init({
      mainElement: 'body',
      onRefresh() {
        debouncedReset();
      },
      iconArrow: ReactDOMServer.renderToString(<div className="ptr down-arrow" />),
      iconRefreshing: ReactDOMServer.renderToString(<div className="top-spinner" />),
    });

    return () => {
      window.removeEventListener('orientationchange', handleOrientation);
      window.addEventListener('mobileAuth', mobileAuthenticate, false);
      window.addEventListener('appUserLeft', appUserLeft, false);
      window.removeEventListener('newPlayerPlaying', newPlayerPlayingEvent);

      document.getElementsByTagName('body')[0].removeEventListener('gesturestart', gestureStart, false);

      PullToRefresh.destroyAll();
    };
  }, [appUserLeft, handleOrientation, isWebview, mobileAuthenticate, showHeader, resetComponent, resetPlayerSource]);

  useEffect(() => {
    if (mobileLoginDate) {
      resetComponent();
      return () => dispatch(resetStreamsData({ trigger: 'PastBroadcasts@effect' }));
    }
    return undefined;
  }, [resetComponent, dispatch, mobileLoginDate, accountId]);

  const RecordingRender = isWebview ? RecordingClickToPlay : RecordingLink;
  const responsiveGridClass = streams.length > 0 ? 'responsive-grid' : '';

  return (
    <div className="grid-broadcasts-content">
      {!isWebview && (
        <div className="component-header">
          <h2 className="component-title">Recordings</h2>
          <div className="filters">
            <Search label="Title" value={searchInput} onChange={handleSearchChange} tipText="Min 2 characters" />
            <ChannelSelectWithFilter
              value={channelFilter}
              onChange={setChannelFilter}
              filterAllValueStatus={DEFAULT_FILTER_OPTION.CHANNEL}
              placeholderText="All Channels"
              storageKey={FILTER_STORAGE_KEY.CHANNEL}
            />
            {(!mediaUploadEnabled || canViewAllRecordings) && (
              <UserSelectWithFilter value={hostFilter} onChange={setHostFilter} />
            )}
          </div>
        </div>
      )}
      <ul id="past-broadcasts" className={`flex-container ${responsiveGridClass}`}>
        {streams.map((stream) => (
          <RecordingRender key={`${stream.id}-${stream.streamCode}`} stream={stream} />
        ))}
        {(isStreamsLoading || isMobileFirstLoad) && <Loader />}
      </ul>

      {!isStreamsLoading && streams.length === 0 && searchInput && (
        <EmptyResult
          imgSrc="/no-recordings.png"
          title="No Recordings Found"
          message={`It doesn’t look like there are any recordings with “${searchInput}” it their title.`}
        />
      )}

      {!isStreamsLoading && streams.length === 0 && !searchInput && !isMobileFirstLoad && (
        <NoPastBroadcastsFound
          isWebview={isWebview}
          loadBroadcasts={loadBroadcasts}
          reAuth={reAuth}
          trackEvent={trackEvent}
        />
      )}

      {!isLastPage && streams.length >= pageSize && !isStreamsLoading && (
        <BottomScrollListener onBottomReached={debouncedLoadStreams}>
          <Loader />
        </BottomScrollListener>
      )}
    </div>
  );
};

PastBroadcasts.defaultProps = {
  appLogin: null,
  isWebview: false,
  loadBroadcasts: null,
  showHeader: null,
};

PastBroadcasts.propTypes = {
  appLogin: PropTypes.func,
  getScreenOrientation: PropTypes.func.isRequired,
  isWebview: PropTypes.bool,
  loadBroadcasts: PropTypes.func,
  reAuth: PropTypes.func.isRequired,
  showHeader: PropTypes.func,
  trackEvent: PropTypes.func.isRequired,
};

export default withRouter(PastBroadcasts);
